import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, message, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useAuthenticatedRequest } from '../../UserContext';

interface MfaCodeProps {
  apiUrl: string;
  accessToken: string;
  clientConfigId: string;
}

export function FubMfaCode(props: MfaCodeProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hide = () => {
      if (document.hidden) {
        setVisible(false);
      }
    };
    document.addEventListener('visibilitychange', hide);
    return () => document.removeEventListener('visibilitychange', hide);
  }, []);

  return (
    <div className="crm-mfa-row">
      <Button onClick={() => setVisible(value => !value)} size="small">
        {visible ? 'Hide MFA code' : 'Show MFA code'}
      </Button>
      {visible && <VisibleMfaCode {...props} />}
    </div>
  );
}

function VisibleMfaCode({ apiUrl, accessToken, clientConfigId }: MfaCodeProps) {
  const authenticatedRequest = useAuthenticatedRequest(accessToken);
  const request = useRef(authenticatedRequest);
  useEffect(() => {
    request.current = authenticatedRequest;
  }, [authenticatedRequest]);

  const [current, setCurrent] = useState<{
    code: string;
    deadline: number;
    wallDeadline: number;
    remaining: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let controller: AbortController;
    let refreshTimer: ReturnType<typeof setTimeout>;
    let countdownTimer: ReturnType<typeof setTimeout>;
    let requestTimer: ReturnType<typeof setTimeout>;

    const fetchCode = async () => {
      setCurrent(null);
      clearTimeout(countdownTimer);
      setError(null);
      controller = new AbortController();
      requestTimer = setTimeout(() => controller.abort(), 10_000);
      const started = performance.now();
      const wallStarted = Date.now();
      let refreshDelay = 30_000;
      let retry = true;
      try {
        const response = await request.current(headers =>
          fetch(`${apiUrl}/api/clients/${clientConfigId}/crm/mfa-code`, {
            headers,
            cache: 'no-store',
            signal: controller.signal,
          }),
        );
        const body = await response.json();
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          retry = response.status !== 401 && response.status !== 403;
          throw new Error(body?.error_detail ?? 'Could not load MFA code.');
        }
        if (
          typeof body?.code !== 'string' ||
          !/^\d{6}$/.test(body.code) ||
          !Number.isFinite(body.server_time) ||
          !Number.isFinite(body.expires_at) ||
          body.expires_at <= body.server_time ||
          body.expires_at - body.server_time > 30
        ) {
          throw new Error('Invalid MFA code response. Please try again.');
        }
        const lifetime = (body.expires_at - body.server_time) * 1000;
        const deadline = started + lifetime;
        const wallDeadline = wallStarted + lifetime;
        const timeLeft = Math.min(
          deadline - performance.now(),
          wallDeadline - Date.now(),
        );
        refreshDelay = Math.max(0, timeLeft);
        if (timeLeft <= 0) {
          throw new Error(
            'MFA code expired in transit. Waiting for a new code.',
          );
        }
        const updateCountdown = () => {
          // Wall time catches device sleep; monotonic time catches clock changes.
          const timeLeft = Math.min(
            deadline - performance.now(),
            wallDeadline - Date.now(),
          );
          if (timeLeft <= 0) {
            setCurrent(null);
            return;
          }
          setCurrent({
            code: body.code,
            deadline,
            wallDeadline,
            remaining: Math.ceil(timeLeft / 1000),
          });
          countdownTimer = setTimeout(updateCountdown, Math.min(250, timeLeft));
        };
        updateCountdown();
      } catch (err) {
        if (!cancelled) {
          setCurrent(null);
          setError(
            err instanceof Error && err.name !== 'AbortError'
              ? err.message
              : 'Could not load a current MFA code. Hide and show to retry.',
          );
        }
      } finally {
        clearTimeout(requestTimer);
        if (!cancelled) {
          if (retry) {
            refreshTimer = setTimeout(() => void fetchCode(), refreshDelay);
          }
        }
      }
    };

    void fetchCode();
    return () => {
      cancelled = true;
      controller?.abort();
      clearTimeout(refreshTimer);
      clearTimeout(requestTimer);
      clearTimeout(countdownTimer);
    };
  }, [apiUrl, clientConfigId]);

  const copy = async () => {
    if (
      !current ||
      performance.now() >= current.deadline ||
      Date.now() >= current.wallDeadline ||
      document.hidden
    ) {
      setCurrent(null);
      return;
    }
    try {
      await navigator.clipboard.writeText(current.code);
      void message.success('MFA code copied.');
    } catch {
      setError('Could not copy the MFA code. Please copy it manually.');
    }
  };

  return (
    <div className="crm-mfa-result">
      {error && <Alert message={error} showIcon type="error" />}
      {current ? (
        <Space size="middle" wrap>
          <div className="crm-mfa-value">
            <Typography.Text className="crm-mfa-digits">
              {current.code}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              onClick={() => void copy()}
              size="small"
              type="text"
            >
              Copy code
            </Button>
          </div>
          <Typography.Text type="secondary">
            Expires in {current.remaining}s
          </Typography.Text>
        </Space>
      ) : !error ? (
        <Typography.Text type="secondary">
          Waiting for a current MFA code…
        </Typography.Text>
      ) : null}
    </div>
  );
}
